import {useShopId} from '@aboutyou/config';
import {useCurrencyConfig, useLocale} from '@aboutyou/i18n';
import {
  ArticleDetailPageContext,
  BrandDetailPageContext,
  BrandDiscoverPageContext,
  CategoryDetailPageContext,
  CustomPageContext,
  TrackingPageTypeWithId,
  TrackingPageTypeWithoutId,
} from '@aboutyou/legacy-tracking-sdk';
import {useCurrentPageType, useLocation} from '@aboutyou/router';
import {
  GTMTrackingClientProvider,
  PageTrackingContextProvider,
} from '@aboutyou/tracking';
import React, {FC} from 'react';
import {useBrandDiscoverIdentifier} from '../backend-services/BrandHub/utils/useBrandDiscoverIdentifier';
import {useBrandShopIdentifier} from '../backend-services/BrandHub/utils/useBrandShopIdentifier';
import {useCategoryId} from '../backend-services/CategoryStream/utils';
import {useCustomPageId} from '../backend-services/CustomPage/useCustomPageId';
import {useProductIdFromSlug} from '../features/ProductUrl';

export const TrackingProvider: FC<
  React.PropsWithChildren<{documentReferrer: string}>
> = ({documentReferrer, children}) => {
  const shopId = useShopId();
  const {pathname, search} = useLocation();
  const pageTrackingInfo = useTrackingByPage();
  const locale = useLocale();
  const {iso} = useCurrencyConfig();

  return (
    <PageTrackingContextProvider
      path={pathname}
      search={search}
      {...pageTrackingInfo}
    >
      <GTMTrackingClientProvider
        shopIds={{shopId}}
        referrer={documentReferrer}
        locale={locale}
        currency={iso}
        search={search}
      >
        {children}
      </GTMTrackingClientProvider>
    </PageTrackingContextProvider>
  );
};

type TrackingPages =
  | ArticleDetailPageContext
  | BrandDetailPageContext
  | BrandDiscoverPageContext
  | CategoryDetailPageContext
  | CustomPageContext
  | {
      readonly type: TrackingPageTypeWithoutId;
      readonly id?: undefined;
    };

const useTrackingByPage = (): TrackingPages => {
  // TODO: As soon as we add more pages we should rethink the approach of using multiple hooks for all pages. Maybe it will perform better if we only have one hook. https://aboutyou.atlassian.net/browse/ASFE-6527
  const categoryId = useCategoryId();
  const articleDetailId = useProductIdFromSlug();
  const brandId = useBrandShopIdentifier();
  const brandDiscoverId = useBrandDiscoverIdentifier();
  const pageType = useCurrentPageType();
  const customPageId = useCustomPageId();

  switch (pageType) {
    case 'category':
      return {
        id: categoryId as number,
        type: TrackingPageTypeWithId.Category,
      } as CategoryDetailPageContext;

    case 'articleDetail':
      return {
        id: articleDetailId,
        type: TrackingPageTypeWithId.ADP,
      } as ArticleDetailPageContext;

    case 'brandShop':
      return {
        id: brandId.id,
        type: TrackingPageTypeWithId.Brand,
      } as BrandDetailPageContext;

    case 'brandDiscover':
      return {
        id: brandDiscoverId.id,
        type: TrackingPageTypeWithId.BrandDiscover,
      } as BrandDiscoverPageContext;

    case 'customPage':
      return {
        id: customPageId as number,
        type: TrackingPageTypeWithId.CustomPage,
      } as CustomPageContext;

    case 'teaserPage':
      return {
        type: TrackingPageTypeWithoutId.Teaser,
      };

    case 'basket':
      return {
        type: TrackingPageTypeWithoutId.Basket,
      };

    case 'wishlist':
      return {
        type: TrackingPageTypeWithoutId.Wishlist,
      };
    case 'similarProducts':
      return {
        type: TrackingPageTypeWithoutId.ProductRecoPage,
      };
    case 'home':
      return {
        type: TrackingPageTypeWithoutId.Browse,
      };

    case 'couponLandingPage': {
      return {
        type: TrackingPageTypeWithoutId.CouponLandingPage,
      };
    }

    default:
      return {
        type: TrackingPageTypeWithoutId.Other,
      };
  }
};
